import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';

const defaultGetOptionId = (option) => option.id;
const defaultGetOptionLabel = (option) => option.label;
const defaultGetOptionDescription = (option) => option.description;

export default function MultiSelectDialog({
  open,
  onClose,
  options = [],
  selectedValues = [],
  onApply,
  title = 'Select options',
  description,
  searchLabel = 'Search options',
  emptyLabel = 'No options available',
  loading = false,
  loadingLabel = 'Loading',
  confirmLabel = 'Apply',
  cancelLabel = 'Cancel',
  clearLabel = 'Clear',
  closeLabel = 'Close',
  showClear = false,
  getOptionId = defaultGetOptionId,
  getOptionLabel = defaultGetOptionLabel,
  getOptionDescription = defaultGetOptionDescription,
  optionRenderer,
  maxWidth = 'sm',
  fullWidth = true,
  onValidationError,
  validationMessage,
  required = false,
}) {
  const [pendingValues, setPendingValues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open) {
      setPendingValues([...selectedValues]);
      setSearchQuery('');
      setValidationError('');
    }
  }, [open, selectedValues]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) => {
      const label = getOptionLabel(option).toLowerCase();
      const description = getOptionDescription(option).toLowerCase();
      return label.includes(normalizedQuery) || description.includes(normalizedQuery);
    });
  }, [getOptionDescription, getOptionLabel, options, searchQuery]);

  const toggleValue = (value) => {
    setPendingValues((current) =>
      current.includes(value)
        ? current.filter((currentValue) => currentValue !== value)
        : [...current, value]
    );
    setValidationError('');
  };

  const handleApply = () => {
    if (required && pendingValues.length === 0) {
      const message = validationMessage || 'Select at least one option.';
      setValidationError(message);
      onValidationError?.();
      return;
    }
    onApply([...pendingValues]);
  };

  const handleClear = () => {
    setPendingValues([]);
    setValidationError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 'calc(100vh - 32px)', sm: 520 },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <IconButton onClick={onClose} aria-label={closeLabel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        {description && (
          <DialogContentText>{description}</DialogContentText>
        )}
        {options.length > 0 && (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={searchLabel}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexShrink: 0 }}
          />
        )}
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loadingLabel}
            </Typography>
          </Box>
        ) : filteredOptions.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          </Box>
        ) : (
          <List
            dense
            sx={{
              mt: 0.5,
              maxHeight: { xs: 310, sm: 320 },
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              flex: 1,
            }}
          >
            {filteredOptions.map((option) => {
              const value = getOptionId(option);
              const label = getOptionLabel(option);
              const descriptionText = getOptionDescription(option);
              const checked = pendingValues.includes(value);

              return (
                <ListItemButton
                  key={value}
                  selected={checked}
                  disabled={option.disabled}
                  onClick={() => toggleValue(value)}
                  sx={{ minHeight: 56 }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={option.disabled}
                    inputProps={{ 'aria-label': label }}
                    sx={{ mr: 1 }}
                  />
                  {optionRenderer ? (
                    optionRenderer(option, checked)
                  ) : (
                    <ListItemText
                      primary={label}
                      secondary={descriptionText || undefined}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
        {validationError && (
          <Typography variant="caption" color="error">
            {validationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          gap: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        {showClear && (
          <Button onClick={handleClear} color="inherit">
            {clearLabel}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="inherit">
          {cancelLabel}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
